// third-party
import { createSlice } from '@reduxjs/toolkit';
import { devConsolelog } from 'helpers/utilityFunctions';

// project imports
import { returnFiltersQueryString } from 'helpers/objectFunctions';
import { dispatch } from 'store';
import axios from 'utils/axios';
import { BOOKING_TYPE, CATEGORY_ACCOMODATION, CATEGORY_FLIGHTS, PAX_TYPE } from 'views/pages/booking-requests/constant';
import { openSnackbar } from './snackbar';
import { COUPONS_API_DOMAIN } from 'config';
import { format, isValid, parseISO } from 'date-fns';

const DEFAULT_ERROR_MESSAGE = 'Something Went Wrong';
const PRODUCTS_ENUM = ['Flights', 'Hotels', 'Holidays', 'Visa', 'All']; // the enums are indexed based example HOTELS is 1 as in array index

const initialState = {
    couponsData: {},
    couponsLoading: false,
    couponsCategoriesLoading: false,
    couponCategoriesData: [],
    addCouponsData: {},
    viewHistoryLoading: false,
    viewHistoryData: []
};

const mapProductToEnum = (index) => {
    const numberedIndex = Number(index);
    if (numberedIndex >= PRODUCTS_ENUM.length) return false;
    return PRODUCTS_ENUM[numberedIndex];
};
const isValidISODate = (dateString) => {
    if (!dateString || Number.isNaN(Date.parse(dateString))) {
        return false;
    }

    const parsedDate = parseISO(dateString);
    return isValid(parsedDate);
};
const mapValueOrEmpty = (value) => (value === 0 ? 0 : value || '');
const mapValidity = (fromDate, toDate) => {
    if (fromDate && toDate && isValidISODate(fromDate) && isValidISODate(toDate)) {
        return `${format(new Date(fromDate), 'do MMMM yyyy')} - ${format(new Date(toDate), 'do MMMM yyyy')}`;
    }
    return '';
};
const maptravelDate = (fromDate, toDate) => {
    if (fromDate && toDate) {
        if (Number.isNaN(Date.parse(fromDate)) && Number.isNaN(Date.parse(toDate))) return '';
        return `${format(new Date(fromDate), 'do MMMM yyyy')} - ${format(new Date(toDate), 'do MMMM yyyy')}`;
    }
    return '';
};
const mapCreatedAt = (date) => {
    if (date) {
        if (!isValidISODate(date)) return { createdDate: '', createdTime: '' };
        return { createdDate: format(new Date(date), 'do MMMM yyyy'), createdTime: format(new Date(date), 'hh:mm aa') };
    }
    return { createdDate: '', createdTime: '' };
};
const couponsMapper = (data, key) => {
    switch (key) {
        case 'availableCoupons': {
            return data?.map((item) => ({
                id: mapValueOrEmpty(item?.id),
                couponName: mapValueOrEmpty(item?.name),
                description: mapValueOrEmpty(item?.description),
                validity: mapValidity(item?.validFrom, item?.validTill),
                travelDate: maptravelDate(item?.travelStartDate, item?.travelEndDate),
                product: mapValueOrEmpty(mapProductToEnum(item?.product)),
                maxUsage: mapValueOrEmpty(item?.maxUsage),
                code: mapValueOrEmpty(item?.code)
            }));
        }
        case 'allocatedCoupons': {
            return data?.map((item) => ({
                id: mapValueOrEmpty(item?.id),
                couponName: mapValueOrEmpty(item?.name),
                description: mapValueOrEmpty(item?.description),
                validity: mapValidity(item?.validFrom, item?.validTill),
                travelDate: maptravelDate(item?.travelStartDate, item?.travelEndDate),
                product: mapValueOrEmpty(mapProductToEnum(item?.product)),
                maxUsage: mapValueOrEmpty(item?.maxUsage),
                code: mapValueOrEmpty(item?.code)
            }));
        }
        default: {
            return [];
        }
    }
};
const viewHistoryMapper = (data) => {
    if (data && typeof data === 'object' && Object?.keys(data)?.length > 0) {
        return [
            {
                code: mapValueOrEmpty(data?.code),
                description: mapValueOrEmpty(data?.description),
                name: mapValueOrEmpty(data?.name),
                couponUsageCount: data?.organization_coupon_limit_counters?.[0]?.counter,
                maxUsageCount: data?.maxUsagePerOrg,
                createdBy: mapValueOrEmpty(data?.createdBy),
                createdAt: mapCreatedAt(data?.createdAt)?.createdDate,
                createdTime: mapCreatedAt(data?.createdAt)?.createdTime,
                category: mapValueOrEmpty(data?.coupon_assignment?.category)
            }
        ];
    }
    return [];
};

const slice = createSlice({
    name: 'coupons',
    initialState,
    reducers: {
        setIsloading(state, action) {
            state.couponsLoading = action.payload;
        },
        setCouponsData(state, action) {
            const { org_active_coupons: allocatedCouponsCopy = [], new_coupons: availableCouponsCopy = [] } = action.payload || {};
            const availableCoupons = couponsMapper(availableCouponsCopy, 'availableCoupons');
            const allocatedCoupons = couponsMapper(allocatedCouponsCopy, 'allocatedCoupons');
            state.couponsData = {
                allocatedCoupons,
                availableCoupons
            };
        },
        setCouponsCategoriesLoading(state, action) {
            state.couponsCategoriesLoading = action.payload;
        },
        setCouponCategoriesData(state, action) {
            state.couponCategoriesData = action.payload;
        },
        setAddCouponsData(state, action) {
            state.addCouponsData = action.payload;
        },
        setViewHistoryLoading(state, action) {
            state.viewHistoryLoading = action.payload;
        },
        setViewHistoryData(state, action) {
            const historyFinalData = viewHistoryMapper(action.payload);
            state.viewHistoryData = historyFinalData;
        },
        resetAddViewData(state) {
            state.addCouponsData = {};
            state.viewHistoryData = [];
        },
        resetToInitialState() {
            return initialState;
        }
    }
});

// Reducer
export default slice.reducer;
export const {
    setCouponsData,
    setIsloading,
    setCouponsCategoriesLoading,
    setCouponCategoriesData,
    setAddCouponsData,
    setViewHistoryLoading,
    setViewHistoryData,
    resetAddViewData,
    resetToInitialState
} = slice.actions;
export function getCouponsList({ orgCode, visibleOnAB }) {
    const getUrl = `${COUPONS_API_DOMAIN}api/v2/ab/coupons/coupon-list/?visibleOnAB=${visibleOnAB}&orgCode=${orgCode}`;
    return async () => {
        try {
            dispatch(setIsloading(true));
            const response = await axios.get(getUrl, {
                headers: {
                    source: 'mweb'
                }
            });
            devConsolelog('getCouponsList', response.data);
            dispatch(setCouponsData(response.data));
        } catch (error) {
            dispatch(
                openSnackbar({
                    open: true,
                    message: error?.message || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        } finally {
            dispatch(setIsloading(false));
        }
    };
}
export function getCouponsCategories() {
    const getUrl = `${COUPONS_API_DOMAIN}api/v2/ab/coupons/categories/`;
    return async () => {
        try {
            dispatch(setCouponsCategoriesLoading(true));
            const response = await axios.get(getUrl, {
                headers: {
                    source: 'mweb'
                }
            });
            dispatch(setCouponCategoriesData(response.data));
        } catch (error) {
            dispatch(
                openSnackbar({
                    open: true,
                    message: error?.message || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        } finally {
            dispatch(setCouponsCategoriesLoading(false));
        }
    };
}

export const getCouponsViewHistory = ({ couponCode, orgCode }) => {
    const getUrl = `${COUPONS_API_DOMAIN}api/v2/ab/coupons/coupon/${couponCode}/?orgCode=${orgCode}`;
    return async () => {
        try {
            dispatch(setViewHistoryLoading(true));
            const response = await axios.get(getUrl, {
                headers: {
                    source: 'mweb'
                }
            });
            dispatch(setViewHistoryData(response.data));
        } catch (error) {
            dispatch(
                openSnackbar({
                    open: true,
                    message: error?.message || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        } finally {
            dispatch(setViewHistoryLoading(false));
        }
    };
};

export const addCoupons = (payload) => {
    const postUrl = `${COUPONS_API_DOMAIN}api/v2/ab/coupons/map-org/`;
    return async () => {
        try {
            dispatch(setIsloading(true));
            const response = await axios.post(postUrl, payload, {
                headers: {
                    source: 'mweb'
                }
            });
            dispatch(setAddCouponsData(response.data));
        } catch (error) {
            dispatch(
                openSnackbar({
                    open: true,
                    message: error?.message || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        } finally {
            dispatch(setIsloading(false));
        }
    };
};
